import { useState, useRef } from 'react'
import { postAPI, imageLibraryAPI } from '../../../services/api'

export default function CreatePostPanel({
  keywords,
  account,
  onClose,
  onCreated,
  selectedDate,
  inline = false,
}) {
  const [mode, setMode] = useState(null) // null | 'manual' | 'keyword'
  const [form, setForm] = useState({ title: '', content: '', imageUrl: '' })
  const [saving, setSaving] = useState(false)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [selectedKeywordId, setSelectedKeywordId] = useState(null)
  const [generating, setGenerating] = useState(false)

  // 이미지 라이브러리
  const [showLibrary, setShowLibrary] = useState(false)
  const [libraryImages, setLibraryImages] = useState([])
  const [libraryLoading, setLibraryLoading] = useState(false)
  const fileInputRef = useRef(null)

  const loadLibrary = async () => {
    if (!account?.id) return
    setLibraryLoading(true)
    try {
      const res = await imageLibraryAPI.getMyImages(account.id)
      if (res.success) setLibraryImages(res.data || [])
    } catch (err) {
      console.error('라이브러리 로드 실패:', err)
    } finally {
      setLibraryLoading(false)
    }
  }

  const handleToggleLibrary = () => {
    if (!showLibrary) loadLibrary()
    setShowLibrary(!showLibrary)
  }

  const handleLibrarySelect = (img) => {
    setForm(prev => ({ ...prev, imageUrl: img.imageUrl || img.url }))
    setImagePreview(img.imageUrl || img.url)
    setImageFile(null)
    setShowLibrary(false)
  }

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImageFile(file)
    setForm(prev => ({ ...prev, imageUrl: '' }))
    const reader = new FileReader()
    reader.onload = (ev) => setImagePreview(ev.target.result)
    reader.readAsDataURL(file)
  }

  const handleCreate = async () => {
    if (!form.title.trim()) { alert('제목을 입력해주세요.'); return }
    try {
      setSaving(true)
      const res = await postAPI.create({
        title: form.title,
        content: form.content,
        imageUrl: form.imageUrl,
        accountId: account?.id,
        targetDate: selectedDate || undefined
      })
      if (res.success) {
        let newPost = res.data
        if (imageFile && newPost.id) {
          try {
            const imgRes = await postAPI.uploadImage(newPost.id, imageFile)
            if (imgRes.success) newPost = imgRes.data
          } catch { /* ignore */ }
        }
        onCreated(newPost)
      }
    } catch (err) {
      alert('등록 실패: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleGenerateSingle = async () => {
    if (!account?.id) { alert('계정 정보가 필요합니다.'); return }
    try {
      setGenerating(true)
      const res = await postAPI.generateSingle({
        accountId: account.id,
        targetDate: selectedDate || undefined
      })
      if (res.success && res.data) {
        onCreated(res.data)
      } else {
        alert('생성 실패: ' + (res.message || '알 수 없는 오류'))
      }
    } catch (err) {
      alert('생성 실패: ' + (err.message || '알 수 없는 오류'))
    } finally {
      setGenerating(false)
    }
  }

  const content = (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* 헤더 */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
        <h3 className="font-bold dark:text-white flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">add_circle</span>
          {selectedDate ? `${selectedDate} 게시물 등록` : '새 게시물 등록'}
        </h3>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
          <span className="material-symbols-outlined dark:text-white">close</span>
        </button>
      </div>

      <div className="p-4">
        {/* 모드 선택 */}
        {!mode && (
          <div className="text-center py-6">
            <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-600 mb-3 block">add_photo_alternate</span>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">등록 방식을 선택하세요</p>
            <div className="flex flex-col gap-3">
              <button onClick={() => setMode('manual')}
                className="w-full px-4 py-3 text-sm font-semibold text-white bg-primary rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-lg">edit_note</span> 직접 등록
              </button>
              <button onClick={() => setMode('keyword')}
                className="w-full px-4 py-3 text-sm font-semibold text-primary bg-primary/10 border border-primary/30 rounded-xl hover:bg-primary/20 transition-colors flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-lg">auto_awesome</span> 키워드로 생성
              </button>
            </div>
          </div>
        )}

        {/* 직접 등록 */}
        {mode === 'manual' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <button onClick={() => setMode(null)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
                <span className="material-symbols-outlined text-slate-500">arrow_back</span>
              </button>
              <h4 className="font-bold dark:text-white">직접 등록</h4>
            </div>

            {/* 이미지 영역 */}
            <div className="relative w-full aspect-video bg-slate-100 dark:bg-slate-800 rounded-xl overflow-hidden">
              {imagePreview ? (
                <img src={imagePreview} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center">
                  <span className="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-600">image</span>
                  <p className="text-xs text-slate-400 mt-1">이미지를 선택하세요</p>
                </div>
              )}
              <div className="absolute bottom-2 right-2 flex gap-1">
                <label className="px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-xs font-semibold rounded-lg cursor-pointer transition-colors flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">upload_file</span>
                  파일
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                </label>
                <button onClick={handleToggleLibrary}
                  className="px-3 py-1.5 bg-violet-500/80 hover:bg-violet-500 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">photo_library</span>
                  AI 라이브러리
                </button>
              </div>
            </div>

            {/* AI 이미지 라이브러리 */}
            {showLibrary && (
              <div className="border border-violet-200 dark:border-violet-800 rounded-xl p-3 bg-violet-50/50 dark:bg-violet-900/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-violet-600 dark:text-violet-400">AI 이미지 라이브러리</span>
                  <button onClick={() => setShowLibrary(false)} className="p-0.5 hover:bg-violet-200 dark:hover:bg-violet-800 rounded">
                    <span className="material-symbols-outlined text-sm text-violet-500">close</span>
                  </button>
                </div>
                {libraryLoading ? (
                  <div className="text-center py-4 text-xs text-slate-400">로딩 중...</div>
                ) : libraryImages.length === 0 ? (
                  <div className="text-center py-4 text-xs text-slate-400">생성된 AI 이미지가 없습니다</div>
                ) : (
                  <div className="grid grid-cols-3 gap-1.5 max-h-48 overflow-y-auto">
                    {libraryImages.map(img => (
                      <div key={img.id} onClick={() => handleLibrarySelect(img)}
                        className="aspect-square rounded-lg overflow-hidden cursor-pointer ring-2 ring-transparent hover:ring-violet-400 transition-all">
                        <img src={img.imageUrl || img.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">제목</label>
              <input type="text" value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="게시물 제목을 입력하세요"
                className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 block">캡션</label>
              <textarea value={form.content}
                onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="캡션을 입력하세요 (해시태그 포함)" rows={4}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none resize-none" />
            </div>

            <div className="flex gap-2">
              <button onClick={handleCreate} disabled={saving}
                className="flex-1 py-2.5 bg-primary text-white font-semibold text-sm rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-1">
                {saving ? (<><span className="material-symbols-outlined text-sm animate-spin">progress_activity</span> 등록 중...</>) : (<><span className="material-symbols-outlined text-sm">check</span> 등록</>)}
              </button>
              <button onClick={() => setMode(null)}
                className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-semibold text-sm rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                취소
              </button>
            </div>
          </div>
        )}

        {/* AI 자동 생성 */}
        {mode === 'keyword' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <button onClick={() => setMode(null)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
                <span className="material-symbols-outlined text-slate-500">arrow_back</span>
              </button>
              <h4 className="font-bold dark:text-white">AI 자동 생성</h4>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 text-xs text-slate-500 dark:text-slate-400">
              <span className="material-symbols-outlined text-sm align-middle mr-1">info</span>
              자동화 설정의 상호명/업종 정보를 기반으로 AI가 캡션과 이미지를 자동 생성합니다.
            </div>

            <div className="flex gap-2">
              <button onClick={handleGenerateSingle} disabled={generating}
                className="flex-1 py-2.5 bg-primary text-white font-semibold text-sm rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-1">
                {generating ? (<><span className="material-symbols-outlined text-sm animate-spin">progress_activity</span> 생성 중...</>) : (<><span className="material-symbols-outlined text-sm">auto_awesome</span> AI 생성</>)}
              </button>
              <button onClick={() => setMode(null)}
                className="px-4 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-semibold text-sm rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
                취소
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  if (inline) return content

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="max-w-lg mx-4 w-full" onClick={e => e.stopPropagation()}>
        {content}
      </div>
    </div>
  )
}
